/* 
STATUS (2026-02-05):

✔ Working & relied upon:
- Single-slot commission items (_slots_required = 1)
- Rush slot math (daily slots + rush credits)
- Quantity reduction when over capacity is caused by qty > 1
- Monster Cart + theme checkout both work in normal cases

⚠ Intentionally NOT fully solved:
- Single line items that require multiple slots (e.g. 1 item requiring 3 slots)
- Blocking checkout + messaging for that case caused navigation conflicts
  with Monster Cart and was deferred.

WHY PAUSED:
- Edge case (minority of orders)
- Risk of breaking checkout > benefit right now
- Current behavior fails open (allows checkout) instead of blocking customers

NEXT TIME:
- Revisit multi-slot single-item blocking
- Likely solution: cart-page UX warning + disable checkout button
  (not checkout interception)

Do NOT refactor this file casually — it is tightly coupled to cart behavior.
*/


/* fbp-cart-line-notice.js
   ============================================================
   Cart Line Notice (Monster + Theme safe)
   - Shows a small red note UNDER the product title/link in the cart
   - ONLY for commission items (detected via cart line property _custom_commission="1")
   - Works without data-key attributes (Monster-safe)
   - Idempotent (won’t duplicate)
   - Optional toast after clamp (fbp_clamp_adjusted flag)
   ============================================================
*/
(() => {
  "use strict";

  const NOTICE_CLASS = "fbp-cart-line-notice";
  const NOTICE_TEXT = "Daily availability determined at checkout";

  const COMMISSION_FLAG_KEY = "_custom_commission";

  const DEBOUNCE_MS = 200;

  // ===== Clamp toast =====
  function shouldShowClampToast() {
    try { return sessionStorage.getItem("fbp_clamp_adjusted") === "1"; } catch (_) { return false; }
  }
  function clearClampToastFlag() {
    try { sessionStorage.removeItem("fbp_clamp_adjusted"); } catch (_) {}
  }

  function ensureClampToast(root) {
    if (!root) return null;

    let el = root.querySelector(".fbp-clamp-toast");
    if (el) return el;

    el = document.createElement("div");
    el.className = "fbp-clamp-toast";
    el.textContent = "Quantity updated to match today’s available custom slots.";
    el.style.cssText = `
      margin: 8px 0;
      padding: 8px 10px;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.25;
      border-radius: 8px;
      background: #fff6f6;
      border: 1px solid rgba(0,0,0,0.12);
      color: #b42318;
    `;

    // Put near top of cart content
    root.prepend(el);
    return el;
  }

  function removeClampToast(root) {
    if (!root) return;
    root.querySelectorAll(".fbp-clamp-toast").forEach((n) => {
      try { n.remove(); } catch (_) {}
    });
  }

  // ===== Utilities =====
  function debounce(fn, ms) {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  }

  async function fetchCartSafe() {
    try {
      const r = await fetch("/cart.js", { cache: "no-store", credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch (_) {
      return null;
    }
  }

  function isVisible(el) {
    if (!el) return false;
    const cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden" || cs.opacity === "0") return false;
    // Some overlays report visible but have 0 size; still allow it.
    return true;
  }

  function findCartRoots() {
    const roots = [];

    // Theme drawer (Dawn-ish)
    const themeDrawer =
      document.querySelector("#CartDrawer .drawer__inner") ||
      document.querySelector("cart-drawer .drawer__inner") ||
      document.querySelector(".drawer__inner[role='dialog']");
    if (themeDrawer) roots.push(themeDrawer);

    // Cart page
    const cartPage =
      document.querySelector("cart-items") ||
      document.querySelector('form[action^="/cart"]') ||
      document.querySelector("main");
    if (cartPage) roots.push(cartPage);

    // Monster cart drawer (wide net)
    const monsterCandidates = [
      "#monster-cart",
      "#monster_cart",
      "#monster-upsells-cart",
      ".monster-cart",
      ".monster-cart-drawer",
      ".monster-upsells-cart",
      ".monster-upsells__cart",
      "[data-monster-cart]",
      "[id*='monster'][id*='cart']",
      "[class*='monster'][class*='cart']",
      "div[role='dialog']",
    ]
      .flatMap((sel) => Array.from(document.querySelectorAll(sel)))
      .filter(Boolean);

    const monsterVisible = monsterCandidates.filter((el) => {
      if (el.getAttribute?.("role") === "dialog") {
        const s = `${el.id || ""} ${el.className || ""} ${el.getAttribute("aria-label") || ""}`.toLowerCase();
        const hasMonster =
          s.includes("monster") || el.querySelector?.("[data-monster-cart],[id*='monster'],[class*='monster']");
        const hasCartish = s.includes("cart") || s.includes("bag");
        if (!hasMonster || !hasCartish) return false;
      }
      return isVisible(el);
    });

    if (monsterVisible.length) roots.unshift(monsterVisible[0]);
    else if (monsterCandidates.length) roots.unshift(monsterCandidates[0]);

    return Array.from(new Set(roots)).filter(Boolean);
  }

  function normalizeHandleFromUrl(url) {
    try {
      const u = new URL(url, window.location.origin);
      const part = u.pathname.split("/products/").pop() || "";
      return part.split(/[/?#]/)[0].trim().toLowerCase();
    } catch (_) {
      return "";
    }
  }

  function commissionHandlesFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return new Set();

    const handles = cart.items
      .filter((line) => {
        const props = line.properties || {};
        return String(props[COMMISSION_FLAG_KEY]) === "1";
      })
      .map((line) => (line.handle || "").toLowerCase())
      .filter(Boolean);

    return new Set(handles);
  }

  function ensureNoticeAfterTitleNode(titleNode) {
    if (!titleNode) return;

    // Avoid duplicates: if the very next element is already the notice
    const next = titleNode.nextElementSibling;
    if (next && next.classList?.contains(NOTICE_CLASS)) return;

    // Some markup wraps titles; also check direct children of parent
    const parent = titleNode.parentElement;
    if (parent && parent.querySelector?.(`:scope > .${NOTICE_CLASS}`)) return;

    const note = document.createElement("div");
    note.className = NOTICE_CLASS;
    note.textContent = NOTICE_TEXT;

    // Small, not bold, wraps cleanly
    note.style.cssText = `
      margin-top: 2px;
      font-size: 8px;
      line-height: 1.15;
      color: #b42318;
      font-weight: 400 !important;
      font-family: inherit !important;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
    `;

    titleNode.insertAdjacentElement("afterend", note);
  }

  function removeAllNotices(root) {
    if (!root) return;
    root.querySelectorAll(`.${NOTICE_CLASS}`).forEach((n) => {
      try { n.remove(); } catch (_) {}
    });
  }

  function findBestTitleNodeFromLink(linkEl) {
    if (!linkEl) return null;

    const linkText = (linkEl.textContent || "").trim();
    if (linkText.length) return linkEl;

    const container =
      linkEl.closest(".cart-item") ||
      linkEl.closest(".cart-drawer__item") ||
      linkEl.closest("[data-cart-item]") ||
      linkEl.closest("li") ||
      linkEl.closest("tr") ||
      linkEl.closest("div");

    if (!container) return linkEl;

    const titleSelectors = [
      ".cart-item__name",
      ".cart-item__title",
      ".cart-drawer__item-name",
      ".item-title",
      ".product-title",
      ".line-item-title",
      "[data-cart-item-title]",
      "[data-line-item-title]",
      "a[href*='/products/']",
    ];

    for (const sel of titleSelectors) {
      const n = container.querySelector(sel);
      if (n && (n.textContent || "").trim().length) return n;
    }

    return linkEl;
  }

  async function apply() {
    const cart = await fetchCartSafe();
    const commissionHandles = commissionHandlesFromCart(cart);

    const roots = findCartRoots();
    if (!roots.length) return;

    // Toast should show ONCE across all roots
    const showToast = shouldShowClampToast();

    roots.forEach((root) => {
      if (!root) return;

      // Clamp toast handling
      if (showToast) ensureClampToast(root);
      else removeClampToast(root);

      // If no commission items in cart, remove line notices and stop
      if (!commissionHandles.size) {
        removeAllNotices(root);
        return;
      }

      // Find all product links in this root
      const links = Array.from(root.querySelectorAll("a[href*='/products/']")).filter(Boolean);

      // Remove notes that are no longer under commission items
      root.querySelectorAll(`.${NOTICE_CLASS}`).forEach((note) => {
        const prev = note.previousElementSibling;
        const link =
          prev?.closest?.("a[href*='/products/']") ||
          prev?.querySelector?.("a[href*='/products/']");
        const href = link?.getAttribute?.("href") || "";
        const h = normalizeHandleFromUrl(href);
        if (h && !commissionHandles.has(h)) {
          try { note.remove(); } catch (_) {}
        }
      });

      // Add notes for commission items
      links.forEach((link) => {
        const h = normalizeHandleFromUrl(link.getAttribute("href") || "");
        if (!h || !commissionHandles.has(h)) return;

        const titleNode = findBestTitleNodeFromLink(link);
        ensureNoticeAfterTitleNode(titleNode);
      });
    });

    if (showToast) clearClampToastFlag();
  }

  const scheduleApply = debounce(apply, DEBOUNCE_MS);

  // Public API
  window.FBP_CartLineNotice = {
    refresh() {
      scheduleApply();
    },
  };

  // Keep alive through rerenders
  const mo = new MutationObserver(() => scheduleApply());
  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Initial
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scheduleApply());
  } else {
    scheduleApply();
  }
})();
